import type { Show, ShowHistory, Track } from '@shady-pines/db';
import { isNonEmpty, isNonNil, type Uuid } from '@shady-pines/types';
import { equal } from '@shady-pines/types';
import { useEffect, useMemo } from 'react';
import { useTrackStore } from './track-store';

export const useShowHistoriesForShow = (showId: Uuid<Show>) => {
  const showHistories = useTrackStore((s) => s.showHistories[showId], equal);
  const loading = useTrackStore((s) => s.loading.showHistoriesForShow[showId]);
  const fetchShowHistory = useTrackStore((s) => s.fetchShowHistoriesForShow);
  const hasShowHistory = isNonNil(showHistories);

  useEffect(() => {
    if (hasShowHistory) {
      return;
    }
    console.log('loading show histories', showId);
    void fetchShowHistory(showId);
  }, [fetchShowHistory, hasShowHistory, showId]);

  return useMemo(() => ({ showHistories: showHistories ?? [], loading }), [loading, showHistories]);
};

export const useTrackHistoryForShowHistory = (showHistoryId: Uuid<ShowHistory>) => {
  const trackHistoriesFromShowHistory = useTrackStore(
    (s) =>
      s.trackHistoryByShowHistory[showHistoryId]?.map((t) => ({ ...t, id: t.track_id ?? t.id })),
    equal,
  );
  const fetchTrackHistoryForShowHistory = useTrackStore((s) => s.fetchTrackHistoryForShowHistory);

  const loading = useTrackStore((s) => s.loading.tracksForShowHistory[showHistoryId]);
  const hasTracks = isNonNil(trackHistoriesFromShowHistory);

  useEffect(() => {
    console.log('use track history effect triggered for show history', {
      showHistoryId,
      loading,
      hasTracks,
    });
    if (hasTracks || loading) {
      return;
    }

    console.log('loading tracks for show history', showHistoryId);
    void fetchTrackHistoryForShowHistory(showHistoryId);
  }, [fetchTrackHistoryForShowHistory, hasTracks, loading, showHistoryId]);

  return useMemo(
    () => ({
      trackHistoriesFromShowHistory: trackHistoriesFromShowHistory,
      loading,
    }),
    [loading, trackHistoriesFromShowHistory],
  );
};

export const useTracks = (trackIds: Uuid<Track>[]) => {
  const tracks = useTrackStore((s) => trackIds.map((t) => s.tracksById[t]).filter(isNonNil), equal);
  const tracksLoading = useTrackStore((s) =>
    isNonEmpty(trackIds.filter((id) => s.loading.track[id])),
  );
  const hasAllTracks = tracks.length === trackIds.length;
  const fetchTracks = useTrackStore((s) => s.fetchTracks);

  useEffect(() => {
    if (hasAllTracks) {
      return;
    }
    console.log('loading tracks', trackIds);
    void fetchTracks(trackIds);
  }, [fetchTracks, hasAllTracks, trackIds]);

  return useMemo(
    () => ({
      tracks: tracks,
      loading: tracksLoading,
    }),
    [tracks, tracksLoading],
  );
};

export const useTrack = (trackId: Uuid<Track>) => {
  const track = useTrackStore(
    (s) => s.tracksById[trackId],
    (a, b) => a?.id === b?.id,
  );
  const hasTrack = isNonNil(track);
  const fetchTrack = useTrackStore((s) => s.fetchTrack);
  const loading = useTrackStore((s) => s.loading.track[trackId]);

  useEffect(() => {
    if (hasTrack || loading) {
      return;
    }
    console.log('loading single track', trackId);
    void fetchTrack(trackId);
  }, [fetchTrack, hasTrack, loading, trackId]);

  return useMemo(() => ({ track, loading }), [track, loading]);
};

export const useShowsFeaturedByTrack = (trackId: Uuid<Track>) => {
  const showsFeaturedByTrack = useTrackStore((s) => s.showsFeaturedByTrack[trackId]);
  const loading = useTrackStore((s) => s.loading.showsFeaturedByTrack[trackId]);
  const fetchShowsFeaturedByTrack = useTrackStore((s) => s.fetchShowsFeaturedByTrack);
  const hasShowsFeaturedByTrack = isNonNil(showsFeaturedByTrack);

  useEffect(() => {
    if (hasShowsFeaturedByTrack || loading) {
      return;
    }
    void fetchShowsFeaturedByTrack(trackId);
  }, [fetchShowsFeaturedByTrack, hasShowsFeaturedByTrack, loading, trackId]);

  return useMemo(
    () => ({ showsFeaturedByTrack: showsFeaturedByTrack ?? [], loading }),
    [showsFeaturedByTrack, loading],
  );
};
